import React, { useState, useEffect } from 'react';
import { Veiculo } from '../../types/veiculo';
import { VeiculoChecklist } from '../../types/veiculoExtra';
import { veiculoExtraService } from '../../services/veiculoExtraService';
import { ClipboardCheck, Plus, CheckCircle, AlertTriangle, XCircle, X, Shield, History } from 'lucide-react';

interface Props {
  veiculo: Veiculo | null;
  motoristasList: { id: number; nome: string }[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const VeiculoChecklistModal: React.FC<Props> = ({
  veiculo,
  motoristasList,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState<'novo' | 'historico'>('novo');
  const [history, setHistory] = useState<VeiculoChecklist[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Form State
  const [motoristaId, setMotoristaId] = useState<number | ''>('');
  const [kmInicial, setKmInicial] = useState<number | ''>('');
  const [combustivelNivel, setCombustivelNivel] = useState('cheio');
  const [statusGeral, setStatusGeral] = useState<'aprovado' | 'reprovado' | 'com_observacoes'>('aprovado');
  const [observacoes, setObservacoes] = useState('');

  // Items Checklist
  const [pneusOk, setPneusOk] = useState(true);
  const [freiosOk, setFreiosOk] = useState(true);
  const [luzesOk, setLuzesOk] = useState(true);
  const [oleoOk, setOleoOk] = useState(true);
  const [aguaOk, setAguaOk] = useState(true);
  const [latariaOk, setLatariaOk] = useState(true);
  const [documentacaoOk, setDocumentacaoOk] = useState(true);
  const [equipamentosOk, setEquipamentosOk] = useState(true);

  useEffect(() => {
    if (veiculo && isOpen) {
      setKmInicial(veiculo.km_atual || '');
      setMotoristaId(veiculo.motorista_id || veiculo.agregado_id || '');
      loadHistory();
    }
  }, [veiculo, isOpen]);

  const loadHistory = async () => {
    if (!veiculo) return;
    setLoading(true);
    try {
      const data = await veiculoExtraService.getChecklists(veiculo.id);
      setHistory(data);
    } catch (err) {
      console.error('Erro ao carregar histórico de checklists:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !veiculo) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg('');

    try {
      const selectedMot = motoristasList.find((m) => m.id === Number(motoristaId));
      const newChecklist: VeiculoChecklist = {
        veiculo_id: veiculo.id,
        motorista_id: motoristaId ? Number(motoristaId) : null,
        motorista_nome: selectedMot?.nome || 'Não informado',
        data_checklist: new Date().toISOString(),
        km_inicial: kmInicial ? Number(kmInicial) : null,
        status_geral: statusGeral,
        pneus_ok: pneusOk,
        freios_ok: freiosOk,
        luzes_ok: luzesOk,
        oleo_ok: oleoOk,
        agua_ok: aguaOk,
        combustivel_nivel: combustivelNivel,
        lataria_limpeza_ok: latariaOk,
        documentacao_ok: documentacaoOk,
        equipamentos_seguranca_ok: equipamentosOk,
        observacoes,
      };

      await veiculoExtraService.createChecklist(newChecklist);
      await loadHistory();
      setActiveTab('historico');
      if (onSuccess) onSuccess();
    } catch (err: any) {
      console.error('Erro ao salvar checklist:', err);
      setErrorMsg(err.message || 'Erro ao salvar o checklist pré-viagem.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200">
        {/* Header */}
        <div className="bg-slate-900 px-6 py-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-blue-500/20 rounded-lg border border-blue-500/30 text-blue-400">
              <ClipboardCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-base leading-snug">Checklist Pré-Viagem</h3>
              <p className="text-xs text-slate-400">
                Veículo: <span className="font-semibold text-white">{veiculo.placa || 'Sem Placa'}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs navigation */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-6 pt-3">
          <button
            type="button"
            onClick={() => setActiveTab('novo')}
            className={`px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-2 ${
              activeTab === 'novo'
                ? 'border-blue-600 text-blue-600 bg-white rounded-t-lg'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Plus className="w-4 h-4" />
            Novo Inspeção / Checklist
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('historico')}
            className={`px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-2 ${
              activeTab === 'historico'
                ? 'border-blue-600 text-blue-600 bg-white rounded-t-lg'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <History className="w-4 h-4" />
            Histórico de Checklists ({history.length})
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 max-h-[75vh] overflow-y-auto">
          {activeTab === 'novo' ? (
            <form onSubmit={handleSave} className="space-y-5">
              {errorMsg && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                  {errorMsg}
                </div>
              )}

              {/* Informações Iniciais */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Motorista / Condutor
                  </label>
                  <select
                    value={motoristaId}
                    onChange={(e) => setMotoristaId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">Selecione o motorista...</option>
                    {motoristasList.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Quilometragem (KM Inicial)
                  </label>
                  <input
                    type="number"
                    value={kmInicial}
                    onChange={(e) => setKmInicial(e.target.value ? Number(e.target.value) : '')}
                    placeholder="Ex: 124500"
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                  />
                </div>
              </div>

              {/* Grid de Itens a Checar */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-2 flex items-center gap-1">
                  <Shield className="w-3.5 h-3.5 text-blue-600" />
                  Itens de Verificação Pré-Viagem
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                  <label className="flex items-center gap-2.5 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100/50">
                    <input
                      type="checkbox"
                      checked={pneusOk}
                      onChange={(e) => setPneusOk(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-800">Pneus & Calibragem OK</span>
                  </label>

                  <label className="flex items-center gap-2.5 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100/50">
                    <input
                      type="checkbox"
                      checked={freiosOk}
                      onChange={(e) => setFreiosOk(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-800">Sistema de Freios OK</span>
                  </label>

                  <label className="flex items-center gap-2.5 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100/50">
                    <input
                      type="checkbox"
                      checked={luzesOk}
                      onChange={(e) => setLuzesOk(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-800">Faróis & Setas OK</span>
                  </label>

                  <label className="flex items-center gap-2.5 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100/50">
                    <input
                      type="checkbox"
                      checked={oleoOk}
                      onChange={(e) => setOleoOk(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-800">Nível do Óleo OK</span>
                  </label>

                  <label className="flex items-center gap-2.5 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100/50">
                    <input
                      type="checkbox"
                      checked={aguaOk}
                      onChange={(e) => setAguaOk(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-800">Água do Radiador OK</span>
                  </label>

                  <label className="flex items-center gap-2.5 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100/50">
                    <input
                      type="checkbox"
                      checked={latariaOk}
                      onChange={(e) => setLatariaOk(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-800">Lataria & Limpeza OK</span>
                  </label>

                  <label className="flex items-center gap-2.5 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100/50">
                    <input
                      type="checkbox"
                      checked={documentacaoOk}
                      onChange={(e) => setDocumentacaoOk(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-800">Documentação (CRLV) OK</span>
                  </label>

                  <label className="flex items-center gap-2.5 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100/50">
                    <input
                      type="checkbox"
                      checked={equipamentosOk}
                      onChange={(e) => setEquipamentosOk(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    <span className="text-xs font-semibold text-slate-800">Extintor / Macaco / Triângulo OK</span>
                  </label>
                </div>
              </div>

              {/* Nível de Combustível e Resultado Geral */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Nível de Combustível
                  </label>
                  <select
                    value={combustivelNivel}
                    onChange={(e) => setCombustivelNivel(e.target.value)}
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="cheio">Tanque Cheio (100%)</option>
                    <option value="3/4">3/4 Tanque (75%)</option>
                    <option value="1/2">Meio Tanque (50%)</option>
                    <option value="1/4">1/4 Tanque (25%)</option>
                    <option value="reserva">Reserva (&lt;10%)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Resultado Geral da Inspeção
                  </label>
                  <select
                    value={statusGeral}
                    onChange={(e) => setStatusGeral(e.target.value as any)}
                    className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                  >
                    <option value="aprovado">🟢 Aprovado (Apto para Viagem)</option>
                    <option value="com_observacoes">🟡 Com Observações (Atenção)</option>
                    <option value="reprovado">🔴 Reprovado (Necessita Manutenção)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Observações Gerais
                </label>
                <textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Informe detalhes sobre arranhões existentes, barulhos ou itens a observar..."
                  rows={2}
                  className="w-full text-xs p-2.5 border border-slate-300 rounded-lg bg-white text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow transition-colors flex items-center gap-2"
                >
                  {saving ? 'Gravando...' : 'Finalizar Checklist'}
                </button>
              </div>
            </form>
          ) : (
            /* Tab de Histórico */
            <div className="space-y-4">
              {loading ? (
                <div className="py-8 text-center text-xs text-slate-500">Carregando checklists...</div>
              ) : history.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-500 border border-dashed rounded-xl p-4">
                  Nenhum checklist registrado para este veículo.
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((c) => (
                    <div
                      key={c.id || c.data_checklist}
                      className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2 text-xs"
                    >
                      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                        <span className="font-semibold text-slate-700">
                          {new Date(c.data_checklist).toLocaleString('pt-BR')} - {c.motorista_nome || 'Condutor'}
                        </span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            c.status_geral === 'aprovado'
                              ? 'bg-emerald-100 text-emerald-800'
                              : c.status_geral === 'com_observacoes'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-rose-100 text-rose-800'
                          }`}
                        >
                          {c.status_geral.replace('_', ' ')}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-slate-600">
                        <div>KM: <span className="font-bold text-slate-800">{c.km_inicial ?? 'N/I'}</span></div>
                        <div>Combustível: <span className="font-bold text-slate-800">{c.combustivel_nivel}</span></div>
                        <div>Pneus: {c.pneus_ok ? '✅ OK' : '❌ Atencao'}</div>
                        <div>Freios: {c.freios_ok ? '✅ OK' : '❌ Atencao'}</div>
                      </div>

                      {c.observacoes && (
                        <div className="text-[11px] bg-white p-2 rounded border border-slate-200 text-slate-700">
                          <strong>Obs:</strong> {c.observacoes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
